import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { ApiError } from '../../lib/http';
import { StockAlertApi, stockAlertApi } from '../../lib/stockAlertApi';
import '../VerifyEmailPage/VerifyEmailPage.css';

type Action = 'confirm' | 'unsubscribe';

const COPY: Record<Action, { title: string; busy: string; failed: string }> = {
  confirm: {
    title: 'Back-in-stock alerts',
    busy: 'Confirming your alerts…',
    failed: 'Could not confirm',
  },
  unsubscribe: {
    title: 'Unsubscribe',
    busy: 'Unsubscribing…',
    failed: 'Could not unsubscribe',
  },
};

interface Props {
  readonly action: Action;
  readonly api?: StockAlertApi;
}

/** Where the links in back-in-stock emails land: confirm a guest's alerts, or stop one. */
export default function StockAlertLinkPage({ action, api = stockAlertApi }: Props) {
  const copy = COPY[action];
  useDocumentTitle(copy.title);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [result, setResult] = useState<{ ok: true; message: string } | { ok: false; message: string } | null>(
    token ? null : { ok: false, message: 'This link is incomplete. Open it again from the email.' },
  );
  // Each link is used once per visit, even when React re-runs effects in development.
  const used = useRef(false);

  useEffect(() => {
    if (!token || used.current) return;
    used.current = true;
    const request =
      action === 'confirm'
        ? api
            .confirm(token)
            .then(({ confirmed }) =>
              confirmed
                ? `Confirmed. We'll email you once when ${
                    confirmed === 1 ? 'the item is' : `each of the ${confirmed} items is`
                  } back in stock.`
                : 'These alerts were already confirmed. Nothing else to do.',
            )
        : api.unsubscribe(token).then(() => "You're unsubscribed. We won't email you about this item again.");
    request.then(
      (message) => setResult({ ok: true, message }),
      (error) =>
        setResult({
          ok: false,
          message:
            error instanceof ApiError ? error.message : 'Something went wrong. Please try again later.',
        }),
    );
  }, [action, api, token]);

  return (
    <section className="verify-email">
      <h1 className="verify-email__title">{result && !result.ok ? copy.failed : copy.title}</h1>
      <p className="verify-email__status" role={result ? (result.ok ? 'status' : 'alert') : 'status'}>
        {result ? result.message : copy.busy}
      </p>
      {result && (
        <Link className="verify-email__link" to="/">
          Continue shopping
        </Link>
      )}
    </section>
  );
}
